<template>
  <section :class="['login-container', { 'dark-theme': isDarkMode }]">
    <div class="theme-toggle">
      <button
        type="button"
        class="theme-toggle-btn"
        :title="isDarkMode ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'"
        @click="alternarTema"
      >
        <i :class="isDarkMode ? 'bi bi-sun-fill' : 'bi bi-moon-fill'"></i>
      </button>
    </div>

    <div class="login-shell">
      <main class="login-panel">
        <div class="login-marca">
          <img src="/logo_b.png" alt="SICECOM" class="login-logo" />
        </div>
        <div class="login-card">
          <div class="login-card__header">
            <p class="login-kicker">Acceso seguro</p>
            <h2 class="login-title">Inicio de sesión</h2>
            <p class="login-subtitle">Accede al panel de administración</p>
          </div>

          <form @submit.prevent="onSubmit" class="login-form">
            <div class="campo-grupo">
              <label for="correo" class="form-label">Usuario o correo</label>
              <input
                id="correo"
                v-model.trim="correo"
                type="text"
                class="form-control login-input"
                :class="{ 'is-invalid': error }"
                placeholder="admin@empresa.com"
                required
                autocomplete="username"
                :disabled="cargando"
              />
            </div>

            <div class="campo-grupo">
              <label for="clave" class="form-label">Contraseña</label>
              <div class="clave-grupo">
                <input
                  id="clave"
                  v-model="clave"
                  :type="mostrarClave ? 'text' : 'password'"
                  class="form-control login-input login-input--clave"
                  :class="{ 'is-invalid': error }"
                  placeholder="••••••••"
                  required
                  autocomplete="current-password"
                  :disabled="cargando"
                />
                <button
                  type="button"
                  class="password-toggle-btn"
                  :disabled="cargando"
                  :title="mostrarClave ? 'Ocultar contraseña' : 'Mostrar contraseña'"
                  @click="alternarVisibilidadClave"
                >
                  <i :class="mostrarClave ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                </button>
              </div>
            </div>

            <div v-if="error" class="login-alerta" role="alert">
              <i class="bi bi-exclamation-triangle-fill"></i>
              <span>{{ error }}</span>
            </div>

            <button
              type="submit"
              class="btn login-btn"
              :disabled="cargando"
            >
              <span v-if="cargando" class="login-btn__contenido">
                <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span>Iniciando sesión...</span>
              </span>
              <span v-else class="login-btn__contenido">
                <span>Ingresar al panel</span>
                <i class="bi bi-arrow-right-short"></i>
              </span>
            </button>
          </form>
        </div>
      </main>
    </div>
  </section>
</template>

<script>
import servicioAutenticacion from '@/services/servicioAutenticacion';
import almacenModulos from '@/store/modules';
import gestorTema from '@/store/theme';

export default {
  name: 'VistaInicioSesionAdmin',
  data() {
    return {
      correo: '',
      clave: '',
      cargando: false,
      error: null,
      mostrarClave: false
    };
  },
  computed: {
    isDarkMode() {
      return gestorTema.isDarkMode;
    }
  },
  mounted() {
    gestorTema.applyTheme();
  },
  methods: {
    alternarTema() {
      gestorTema.toggleTheme();
    },
    alternarVisibilidadClave() {
      this.mostrarClave = !this.mostrarClave;
    },
    async onSubmit() {
      this.error = null;
      this.cargando = true;
      try {
        await servicioAutenticacion.iniciarSesion(this.correo, this.clave);

        const perfil = await servicioAutenticacion.obtenerPerfil();
        almacenModulos.commit('setUser', perfil?.user ?? perfil);

        const destino = this.$route.query?.redireccion || '/';
        this.$router.replace(destino);
      } catch (errorInicioSesion) {
        const estado = errorInicioSesion?.response?.status;
        let mensaje = errorInicioSesion?.response?.data?.error || errorInicioSesion?.message || 'No se pudo iniciar sesión';
        if (estado === 401) mensaje = 'Credenciales inválidas';
        if (estado === 500) mensaje = 'Error del servidor. Intente nuevamente';
        this.error = mensaje;
      } finally {
        this.cargando = false;
      }
    }
  },
  beforeUnmount() {
    document.body.classList.remove('dark-theme');
  }
};
</script>

<style scoped>
.login-container {
  --color-primario: #0ea5e9;
  --color-primario-hover: #0284c7;
  --fondo-principal: #f8fafc;
  --fondo-secundario: #ffffff;
  --texto-principal: #0f172a;
  --texto-secundario: #475569;
  --texto-tenue: #94a3b8;
  --borde-principal: #e2e8f0;
  --borde-enfoque: #0ea5e9;
  --exito: #10b981;
  --peligro: #ef4444;
  --sombra-suave: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  --sombra-media: 0 24px 70px -28px rgb(15 23 42 / 0.35);
  min-height: 100vh;
  padding: 32px;
  position: relative;
  overflow: hidden;
  background:
    radial-gradient(circle at top left, rgb(14 165 233 / 0.14), transparent 28%),
    radial-gradient(circle at bottom right, rgb(16 185 129 / 0.1), transparent 24%),
    linear-gradient(180deg, #f8fbff 0%, var(--fondo-principal) 100%);
  color: var(--texto-principal);
}

.login-container.dark-theme {
  --color-primario: #3b82f6;
  --color-primario-hover: #60a5fa;
  --fondo-principal: #020617;
  --fondo-secundario: #111827;
  --texto-principal: #f9fafb;
  --texto-secundario: #94a3b8;
  --texto-tenue: #64748b;
  --borde-principal: #374151;
  --borde-enfoque: #3b82f6;
  --exito: #10b981;
  --peligro: #f87171;
  --sombra-suave: 0 1px 2px 0 rgb(0 0 0 / 0.3);
  --sombra-media: 0 28px 80px -30px rgb(0 0 0 / 0.65);
  background:
    radial-gradient(circle at top left, rgb(59 130 246 / 0.16), transparent 24%),
    radial-gradient(circle at bottom right, rgb(16 185 129 / 0.08), transparent 22%),
    linear-gradient(180deg, #020617 0%, #06111f 100%);
}

.theme-toggle {
  position: absolute;
  top: 24px;
  right: 24px;
  z-index: 2;
}

.theme-toggle-btn {
  width: 46px;
  height: 46px;
  border: 1px solid var(--borde-principal);
  border-radius: 999px;
  background: rgb(255 255 255 / 0.68);
  color: var(--texto-principal);
  box-shadow: var(--sombra-suave);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease;
  backdrop-filter: blur(14px);
}

.dark-theme .theme-toggle-btn {
  background: rgb(17 24 39 / 0.72);
}

.theme-toggle-btn:hover {
  transform: translateY(-1px);
  border-color: var(--color-primario);
}

.login-shell {
  width: min(480px, 100%);
  min-height: calc(100vh - 64px);
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-logo {
  width: clamp(180px, 42vw, 240px);
  max-width: 100%;
  height: auto;
  object-fit: contain;
  display: block;
}

.login-panel {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 24px;
  width: 100%;
}

.login-marca {
  display: flex;
  justify-content: center;
}

.login-card {
  width: 100%;
  padding: 32px;
  border: 1px solid var(--borde-principal);
  border-radius: 28px;
  background: rgb(255 255 255 / 0.82);
  box-shadow: var(--sombra-media);
  backdrop-filter: blur(18px);
}

.dark-theme .login-card {
  background: rgb(17 24 39 / 0.84);
}

.login-card__header {
  margin-bottom: 28px;
}

.login-kicker {
  margin: 0 0 12px;
  color: var(--color-primario);
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.login-title {
  margin: 0;
  font-size: 2rem;
  font-weight: 800;
  letter-spacing: -0.04em;
}

.login-subtitle {
  margin: 10px 0 0;
  color: var(--texto-secundario);
  line-height: 1.6;
}

.login-form {
  display: grid;
  gap: 20px;
}

.campo-grupo {
  display: grid;
  gap: 10px;
}

.form-label {
  margin: 0;
  color: var(--texto-principal);
  font-size: 0.92rem;
  font-weight: 700;
}

.login-input {
  min-height: 54px;
  padding: 14px 16px;
  border: 1px solid var(--borde-principal);
  border-radius: 16px;
  background: var(--fondo-secundario);
  color: var(--texto-principal);
  box-shadow: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.dark-theme .login-input {
  background: rgb(2 6 23 / 0.72);
}

.login-input::placeholder {
  color: var(--texto-tenue);
}

.login-input:focus {
  border-color: var(--borde-enfoque);
  box-shadow: 0 0 0 4px rgb(14 165 233 / 0.15);
}

.dark-theme .login-input:focus {
  box-shadow: 0 0 0 4px rgb(59 130 246 / 0.16);
}

.clave-grupo {
  position: relative;
}

.login-input--clave {
  padding-right: 58px;
}

.password-toggle-btn {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: var(--texto-secundario);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
}

.password-toggle-btn:hover {
  background: rgb(148 163 184 / 0.12);
  color: var(--texto-principal);
}

.password-toggle-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.login-alerta {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 16px;
  border: 1px solid rgb(239 68 68 / 0.25);
  background: rgb(239 68 68 / 0.12);
  color: var(--peligro);
  font-size: 0.95rem;
}

.login-btn {
  width: 100%;
  min-height: 56px;
  border: 0;
  border-radius: 18px;
  background: linear-gradient(135deg, var(--color-primario) 0%, var(--color-primario-hover) 100%);
  color: #ffffff;
  font-size: 1rem;
  font-weight: 700;
  box-shadow: 0 18px 40px -20px rgb(14 165 233 / 0.9);
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

.login-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 22px 44px -20px rgb(14 165 233 / 0.95);
}

.dark-theme .login-btn {
  box-shadow: 0 18px 42px -22px rgb(59 130 246 / 0.95);
}

.login-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.login-btn__contenido {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

@media (max-width: 991.98px) {
  .login-shell {
    min-height: auto;
    padding-top: 56px;
  }
}

@media (max-width: 575.98px) {
  .login-container {
    padding: 18px;
  }

  .theme-toggle {
    top: 18px;
    right: 18px;
  }

  .login-card {
    padding: 24px 18px;
    border-radius: 24px;
  }

  .login-title {
    font-size: 1.7rem;
  }

  .login-logo {
    width: min(190px, 62vw);
  }
}
</style>

